(() => {
  const ARL = window.ARLISHCO = window.ARLISHCO || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealRegistry = new WeakSet();
  const wishlistStorageKey = 'arlishcoWishlistV1';
  const productJsonCache = new Map();
  let cartProgressTimer = null;
  let cartProgressRequest = 0;
  let cartUpdateUnsubscriber = null;
  let toastTimer = null;
  let headerFrame = 0;

  function readBusinessRules() {
    const source = document.getElementById('ArlishcoBusinessRules');
    if (!source) return {};
    try { return JSON.parse(source.textContent || '{}'); } catch (_error) { return {}; }
  }

  const businessRules = readBusinessRules();
  const freeDeliveryThreshold = Number(businessRules.freeShippingThresholdPaise);
  const standardDeliveryFee = Number(businessRules.standardShippingFeePaise);
  const hasDeliveryPricingRules = Number.isFinite(freeDeliveryThreshold)
    && freeDeliveryThreshold > 0
    && Number.isFinite(standardDeliveryFee)
    && standardDeliveryFee >= 0;

  function storefrontRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function formatCartMoney(amount) {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
      .format((Number(amount) || 0) / 100);
  }

  function productHandleFromUrl(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname;
      const match = pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]).toLowerCase() : '';
    } catch (_error) { return ''; }
  }

  function productImageSource(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return image.src || image.url || '';
  }

  async function fetchStorefrontProduct(handle) {
    if (productJsonCache.has(handle)) return productJsonCache.get(handle);
    const request = fetch(`${storefrontRoot()}products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin', headers: { Accept: 'application/json' },
    }).then(async (response) => {
      if (!response.ok) throw new Error('Product unavailable');
      return { ...(await response.json()), handle };
    }).catch((error) => { productJsonCache.delete(handle); throw error; });
    productJsonCache.set(handle, request);
    return request;
  }

  function readWishlist() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(wishlistStorageKey) || '[]');
      if (!Array.isArray(stored)) return [];
      return [...new Set(stored
        .filter((handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(handle))
        .map((handle) => handle.toLowerCase()))].slice(0, 100);
    } catch (_error) { return []; }
  }

  function writeWishlist(handles) {
    try { window.localStorage.setItem(wishlistStorageKey, JSON.stringify(handles)); }
    catch (_error) { /* Private browsing can block storage; shopping remains usable. */ }
  }

  function wishlistIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7-4.5-7-11a4 4 0 0 1 7-2.7A4 4 0 0 1 19 10c0 6.5-7 11-7 11Z"/></svg>';
  }

  function showToast(message) {
    let toast = document.querySelector('[data-arl-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'arl-toast';
      toast.dataset.arlToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.append(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  }

  function updateWishlistInterface(root = document) {
    const saved = new Set(readWishlist());
    root.querySelectorAll?.('[data-arl-wishlist-toggle]').forEach((button) => {
      const handle = button.dataset.productHandle || '';
      const active = saved.has(handle);
      const productName = button.dataset.productTitle || 'this product';
      button.classList.toggle('is-saved', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', active ? `Remove ${productName} from saved items` : `Save ${productName}`);
      const label = button.querySelector('[data-arl-wishlist-label]');
      if (label) label.textContent = active ? 'Saved' : 'Save item';
    });
    document.querySelectorAll('[data-arl-wishlist-count]').forEach((count) => {
      count.textContent = saved.size ? ` (${saved.size})` : '';
    });
  }

  function toggleWishlist(handle, title) {
    if (!handle) return;
    const saved = readWishlist();
    const index = saved.indexOf(handle);
    const adding = index < 0;
    if (adding) saved.unshift(handle); else saved.splice(index, 1);
    writeWishlist(saved);
    updateWishlistInterface();
    showToast(adding ? `${title || 'Product'} saved on this device` : `${title || 'Product'} removed from saved items`);
    document.dispatchEvent(new CustomEvent('arl:wishlist-change'));
  }

  function buildWishlistButton(handle, title, className = '') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `arl-wishlist-toggle ${className}`.trim();
    button.dataset.arlWishlistToggle = '';
    button.dataset.productHandle = handle;
    button.dataset.productTitle = title || 'Product';
    button.setAttribute('aria-pressed', 'false');
    button.innerHTML = `${wishlistIcon()}${className.includes('product') ? '<span data-arl-wishlist-label>Save item</span>' : ''}`;
    button.addEventListener('click', (event) => {
      event.preventDefault(); event.stopPropagation(); toggleWishlist(handle, title);
    });
    return button;
  }

  function cartProgressTargets() {
    return [
      document.querySelector('#main-cart-footer .cart__blocks'),
      document.querySelector('#CartDrawer .cart-drawer__footer'),
    ].filter(Boolean);
  }

  function createCartProgress(target) {
    const progress = document.createElement('section');
    progress.className = 'arl-delivery-progress';
    progress.dataset.arlDeliveryProgress = '';
    progress.setAttribute('role', 'status');
    progress.setAttribute('aria-live', 'polite');
    progress.setAttribute('aria-label', 'Complimentary delivery progress');
    progress.innerHTML = `
      <div class="arl-delivery-progress__content">
        <span class="arl-delivery-progress__icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M3 6.5h11v10H3zM14 10h3l3 3v3.5h-6zM7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg></span>
        <span class="arl-delivery-progress__copy"><strong data-arl-delivery-title></strong><small data-arl-delivery-note></small></span>
      </div>
      <span class="arl-delivery-progress__track" aria-hidden="true"><span data-arl-delivery-bar></span></span>`;
    target.prepend(progress);
    return progress;
  }

  function renderCartProgress(cart) {
    const targets = cartProgressTargets();
    if (!cart || Number(cart.item_count) < 1 || !hasDeliveryPricingRules) {
      document.querySelectorAll('[data-arl-delivery-progress]').forEach((progress) => progress.remove());
      return;
    }
    const total = Number(cart.total_price) || 0;
    const unlocked = total >= freeDeliveryThreshold;
    const remaining = Math.max(freeDeliveryThreshold - total, 0);
    const percentage = Math.min(Math.max((total / freeDeliveryThreshold) * 100, 0), 100);
    targets.forEach((target) => {
      const progress = target.querySelector('[data-arl-delivery-progress]') || createCartProgress(target);
      progress.classList.toggle('is-unlocked', unlocked);
      progress.querySelector('[data-arl-delivery-title]').textContent = unlocked
        ? 'Complimentary delivery unlocked'
        : `Add ${formatCartMoney(remaining)} more for complimentary delivery`;
      progress.querySelector('[data-arl-delivery-note]').textContent = unlocked
        ? 'Standard delivery is included at no extra cost.'
        : `A flat ${formatCartMoney(standardDeliveryFee)} standard-delivery charge applies below ${formatCartMoney(freeDeliveryThreshold)}.`;
      progress.querySelector('[data-arl-delivery-bar]').style.width = `${percentage}%`;
    });
  }

  async function updateCartProgress() {
    if (!cartProgressTargets().length || !hasDeliveryPricingRules) return;
    const request = ++cartProgressRequest;
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        credentials: 'same-origin', headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (request === cartProgressRequest) renderCartProgress(cart);
    } catch (_error) { /* Optional UI must never block cart use. */ }
  }

  function scheduleCartProgress(delay = 90) {
    window.clearTimeout(cartProgressTimer);
    cartProgressTimer = window.setTimeout(updateCartProgress, delay);
  }

  function initCartProgress() {
    scheduleCartProgress(0);
    if (cartUpdateUnsubscriber || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleCartProgress(120));
  }

  async function addVariantToCart(variantId, button, quantity = 1, onSuccess = null) {
    const initialLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer?.getSectionsToRender?.().map((section) => section.id).filter(Boolean) || [];
      const payload = { id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) };
      if (sections.length) { payload.sections = sections.join(','); payload.sections_url = window.location.pathname; }
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || 'Could not add this item.');
      if (typeof onSuccess === 'function') onSuccess();
      if (cartDrawer?.renderContents && result.sections) cartDrawer.renderContents(result);
      else window.location.assign(window.routes?.cart_url || '/cart');
      button.textContent = 'Added';
      showToast('Added to cart');
      scheduleCartProgress(180);
    } catch (error) {
      button.disabled = false; button.textContent = initialLabel;
      showToast(error.message || 'Could not add this item. Please try again.');
      return;
    }
    window.setTimeout(() => { button.disabled = false; button.textContent = initialLabel; }, 1600);
  }

  function initMobileHelpMenu() {
    const drawer = document.querySelector('#menu-drawer .menu-drawer__navigation-container');
    if (!drawer || drawer.querySelector('[data-arl-menu-help]')) return;
    const pageUrl = (handle) => `${storefrontRoot()}pages/${handle}`;
    const help = document.createElement('aside');
    help.className = 'arl-menu-help'; help.dataset.arlMenuHelp = '';
    help.setAttribute('aria-label', 'Help and support');
    help.innerHTML = `<p>HELP &amp; SUPPORT</p><ul class="list-unstyled" role="list">
      <li><a href="${pageUrl('track-order')}"><span>Track Order</span><span aria-hidden="true">→</span></a></li>
      <li><a href="${pageUrl('wishlist')}"><span>Saved Items<span data-arl-wishlist-count></span></span><span aria-hidden="true">→</span></a></li>
      <li><a href="${pageUrl('faq')}"><span>FAQs</span><span aria-hidden="true">→</span></a></li>
      <li><a href="${pageUrl('shipping-information')}"><span>Shipping &amp; Delivery</span><span aria-hidden="true">→</span></a></li>
      <li><a href="${pageUrl('returns-refunds')}"><span>Returns &amp; Replacements</span><span aria-hidden="true">→</span></a></li>
      <li><a href="${pageUrl('warranty-product-support')}"><span>Warranty &amp; Support</span><span aria-hidden="true">→</span></a></li>
      <li><a href="${pageUrl('contact-arlish-co')}"><span>Contact Support</span><span aria-hidden="true">→</span></a></li></ul>`;
    const utilityLinks = drawer.querySelector('.menu-drawer__utility-links');
    if (utilityLinks) utilityLinks.before(help); else drawer.append(help);
  }

  function initReveals(root = document) {
    const elements = Array.from(root.querySelectorAll?.('.arl-reveal') || []).filter((el) => !revealRegistry.has(el));
    if (!elements.length) return;
    elements.forEach((el) => revealRegistry.add(el));
    if (reducedMotion.matches || window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible')); return;
    }
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible'); observer.unobserve(entry.target);
    }), { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    elements.forEach((el) => observer.observe(el));
  }

  function updateHeaderState() { document.documentElement.classList.toggle('arl-scrolled', window.scrollY > 8); }
  function scheduleHeaderState() {
    if (headerFrame) return;
    headerFrame = requestAnimationFrame(() => { headerFrame = 0; updateHeaderState(); });
  }

  function init(root = document) {
    document.documentElement.classList.add('arl-motion-ready');
    initReveals(root); initMobileHelpMenu(); updateWishlistInterface(root); initCartProgress(); updateHeaderState();
  }

  Object.assign(ARL, {
    rules: businessRules, hasDeliveryPricingRules, freeDeliveryThreshold, standardDeliveryFee,
    storefrontRoot, formatCartMoney, productHandleFromUrl, productImageSource, fetchStorefrontProduct,
    readWishlist, writeWishlist, showToast, updateWishlistInterface, toggleWishlist, buildWishlistButton,
    renderCartProgress, updateCartProgress, scheduleCartProgress, addVariantToCart, initReveals,
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  window.addEventListener('scroll', scheduleHeaderState, { passive: true });
  document.addEventListener('arl:wishlist-change', () => updateWishlistInterface());
  document.addEventListener('shopify:section:load', (event) => { init(event.target); scheduleCartProgress(60); });
})();
